import { $, $$, body } from "@utils/dom";
import { on, off } from "@utils/listener";
import { STATE } from "@core/state";

import Cart from "@modules/cart";

import * as cart from '@vendors/shopify/theme-cart/theme-cart';

export const CLASSNAME = "--js-site-cart-opened";
export const CLOSING_CLASSNAME = "--js-site-cart-closing";

class SiteCart {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;

    this.initialized = false;

    this._opened = false;

    this._handleTriggers = this._handleTriggers.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._onCloseCompleted = this._onCloseCompleted.bind(this);
    this._updateCounters = this._updateCounters.bind(this);

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
  }

  get name() {
    return `SiteCart`;
  }

  init() {
    this.initialized = true;

    this._opened = false;

    this.cart = new Cart(this.el, this.emitter, this.open);
    this.cart.init();

    this.triggers = [...$$(`[aria-controls="${this.el.id}"]`)];
    this.container = $('.site-cart__container', this.el);
    this.counters = [...$$('[data-cart-counter]')];

    this._bindEvents();
  }

  destroy() {
    body.classList.remove(CLASSNAME);
    this._unbindEvents();

    this.cart.destroy();

    this.el = null;
    this.emitter = null;

    this.cart = null;

    this.triggers = null;
    this.container = null;
    this.counters = null;

    this._opened = false;

    this.initialized = false;

    this._handleTriggers = null;
    this._handleKeyDown = null;
    this._onCloseCompleted = null;
    this._updateCounters = null;

    this.open = null;
    this.close = null;
  }

  _bindEvents() {
    if (this.triggers) on(this.triggers, "click", this._handleTriggers);
    on(window, "keydown", this._handleKeyDown);

    this.emitter.on("SiteCart.toggle", this._handleTriggers);
    this.emitter.on("SiteCart.update", this._updateCounters);

    this.emitter.on("SiteNav.open", this.close);
    this.emitter.on("SiteWishlist.open", this.close);
  }
  _unbindEvents() {
    if (this.triggers) off(this.triggers, "click", this._handleTriggers);
    off(window, "keydown", this._handleKeyDown);

    this.emitter.off("SiteCart.toggle", this._handleTriggers);
    this.emitter.off("SiteCart.update", this._updateCounters);

    this.emitter.off("SiteNav.open", this.close);
    this.emitter.off("SiteWishlist.open", this.close);
  }

  open() {
    // if SiteCart is already opened, do nothing
    if (this._opened === true) return;
    this._opened = true;

    off(this.container, 'transitionend',this._onCloseCompleted);

    // dispatch to state current status
    STATE.dispatch("SITE_CART", this._opened);

    // inform <body> that SiteCart is opened
    body.classList.add(CLASSNAME);
    body.classList.remove(CLOSING_CLASSNAME);

    // stop page scroll & site-cart open
    this.emitter.emit("SiteScroll.stop", true);
    this.emitter.emit("SiteCart.open");
    this.emitter.emit("DataLayer.view_cart");

    // inform each triggers that SiteCart is opened
    this.triggers.forEach(btn => {
      btn.setAttribute("aria-expanded", true);
      btn.classList.add("is-active");
    });

    // set SiteCart visible for screen readers
    this.el.setAttribute("aria-hidden", false);

    this.el.classList.add('--js-anim-playing');
  }

  close() {
    // if SiteCart isn't opened, do nothing
    if (this._opened === false) return;
    this._opened = false;

    // dispatch to state current status
    STATE.dispatch("SITE_CART", this._opened);

    // inform triggers that SiteCart is closed
    this.triggers.forEach((btn) => {
      btn.setAttribute("aria-expanded", false);
      btn.classList.remove("is-active");
    });

    // inform <body> that SiteCart is closed
    body.classList.remove(CLASSNAME);
    body.classList.add(CLOSING_CLASSNAME);

    // restore previous scrollY
    window.scrollTo({ top: this._scrollY, behavior: "auto" });

    // site-cart close
    this.emitter.emit("SiteCart.close");

    // close callback (should be call at animation's end)
    on(this.container, "transitionend", this._onCloseCompleted);
  }

  _updateCounters(){
    if (!this.counters) return;

    this.counters.forEach((counter)=> {
      counter.classList.add('--is-updating'); 
    });

    cart.getState().then(state => {

      if(state.item_count){
        this.el.classList.remove('--is-empty');
      } else {
        this.el.classList.add('--is-empty');
      }

      this.counters.forEach((counter)=> {
        counter.innerHTML = '';

        if(state.item_count){
          let span = document.createElement('span');
          span.innerHTML = state.item_count;
          counter.append(span);
          counter.classList.remove('--is-empty');
        } else {
          counter.classList.add('--is-empty');
        }

        counter.classList.remove('--is-updating');
      });
      

    });
  }

  toggle() {
    // regular SiteCart toggle handling
    if (this._opened === true) this.close();
    else this.open();
  }

  _handleTriggers(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.toggle();
  }
  
  _handleKeyDown(event) {
    if (this._opened === true && (event.key === "Escape" || event.key === "Esc")) {
      this.close();
    }
  }
  _onCloseCompleted() {
    if (this._opened === true) return;

    body.classList.remove(CLOSING_CLASSNAME);

    // restart page scroll
    if (!STATE['siteWishlistOpen']) this.emitter.emit("SiteScroll.start");

    // hide SiteCart from screen readers
    this.el.setAttribute("aria-hidden", true);

    this.el.classList.remove('--js-anim-playing');
  }


  // getters
  get opened() {
    return this._opened;
  }
}

export default SiteCart;
